import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { userApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { clsx } from 'clsx';

export default function Profile() {
  const { user, signOut } = useAuth();
  const { theme, setTheme: setGlobalTheme, loadThemeFromAPI } = useTheme();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadProfile();
    loadThemeFromAPI();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await userApi.getProfile();
      if (response.success) {
        setProfile(response.data);
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = async (newTheme: 'light' | 'dark' | 'system') => {
    try {
      await setGlobalTheme(newTheme);
    } catch (error) {
      console.error('Error updating theme preference:', error);
      toast.error('Failed to save theme preference');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmationText !== 'DELETE') {
      toast.error('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await userApi.deleteAccount();
      if (response.success) {
        toast.success('Account deleted successfully');
        await signOut();
      } else {
        toast.error(response.error || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Account Information */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Account</h2>

        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
            <span className="text-sm text-gray-900 dark:text-white">{profile?.email || user?.email || 'Not available'}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
            <span className="text-sm text-gray-900 dark:text-white">{profile?.name || 'Not set'}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">Provider</span>
            <span className="text-sm text-gray-900 dark:text-white capitalize">{profile?.identityProvider || 'Unknown'}</span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">User ID</span>
            <span className="text-sm text-gray-400 dark:text-gray-500 font-mono select-all">{user?.id || 'Not available'}</span>
          </div>

          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
            <span className={clsx(
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
              profile?.accountStatus === 'active'
                ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
            )}>
              {profile?.accountStatus || 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Theme Preference */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Theme</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose your preferred appearance</p>
          </div>
          <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-slate-700 rounded-full">
            {(['light', 'dark', 'system'] as const).map((option) => (
              <button
                key={option}
                onClick={() => handleThemeChange(option)}
                className={clsx(
                  'px-3 py-1.5 text-sm font-medium rounded-full transition-colors capitalize',
                  theme === option
                    ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-red-200 dark:border-red-900/50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Delete Account</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Permanently delete your account and all data
            </p>
          </div>
          <button
            onClick={() => setShowDeleteConfirmation(true)}
            className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-full text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => {
                setShowDeleteConfirmation(false);
                setDeleteConfirmationText('');
              }}
            />

            <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full p-6">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                Delete Account?
              </h3>

              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                This will permanently delete your account, chatbots, and all data. This cannot be undone.
              </p>

              <div className="mb-6">
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2 text-center">
                  Type <span className="font-semibold text-red-600 dark:text-red-400">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmationText}
                  onChange={(e) => setDeleteConfirmationText(e.target.value)}
                  className="block w-full border border-gray-300 dark:border-slate-600 rounded-xl px-4 py-2.5 text-center focus:ring-red-500 focus:border-red-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                  placeholder="DELETE"
                  disabled={isDeleting}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmationText('');
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 dark:border-slate-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmationText !== 'DELETE'}
                  className="flex-1 px-4 py-2.5 rounded-full bg-red-600 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
